import React, { useState, useEffect } from "react";
import api from "../../../../api";
import { Card, Row, Col, ProgressBar, Badge } from "react-bootstrap";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { useParams } from "react-router-dom";

const STATUS_COLORS = {
 Approved: "success",
 Pending: "warning",
 "NC Raised": "danger",
 "Retest Required": "info"
};

const CHART_COLORS = {
 Approved: "#28A745",
 Pending: "#FFC107",
 "NC Raised": "#FF4136",
 "Retest Required": "#17A2B8"
};

const TCTab = () => {
 const { id: workOrderId } = useParams();

 const [tcData, setTcData] = useState({
 total: 0,
 approved: 0,
 pending: 0,
 ncRaised: 0,
 retest: 0
 });

 const [recentTCs, setRecentTCs] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const fetchTCRecords = async () => {
 try {
 const res = await api.get("/testing-commissioning", {
 params: { workOrder: workOrderId }
 });

 if (res.data.success) {
 const list = res.data.data.map((r) => {
 // derive final status
 let final = "Pending";
 if (r.items?.some(i => i.status === "NC Raised")) final = "NC Raised";
 else if (r.items?.some(i => i.status === "Retest Required")) final = "Retest Required";
 else if (r.items?.every(i => i.status === "Approved")) final = "Approved";

 return {
 id: r._id,
 number: r.tcNumber || "N/A",
 project: `${r.project?.woNo || "WO-N/A"} - ${r.project?.projectName || r.project?.name || "N/A"}`,
 status: final,
 itemCount: r.items?.length || 0,
 materials: r.items?.map(i => i.equipmentName || i.itemName || "Material") || [],
 testDate: r.testDate ? new Date(r.testDate).toLocaleDateString("en-IN") : "—",
 submittedBy: r.submittedBy?.name || "Technician / Unknown"
 };
 });

 // METRICS
 const approved = list.filter(l => l.status === "Approved").length;
 const ncRaised = list.filter(l => l.status === "NC Raised").length;
 const retest = list.filter(l => l.status === "Retest Required").length;
 const pending = list.filter(l => l.status === "Pending").length;

 setTcData({
 total: list.length,
 approved,
 pending,
 ncRaised,
 retest
 });

 setRecentTCs(list.slice(0, 4));
 }
 } catch (err) {
 console.error("TC Fetch Error:", err);
 } finally {
 setLoading(false);
 }
 };

 if (workOrderId) fetchTCRecords();
 }, [workOrderId]);

 // PIE CHART DATA
 const pieData = Object.keys(CHART_COLORS).map(key => ({
 name: key,
 value: tcData[key.toLowerCase().replace(" ", "")] || 0
 }));

 const approvalPercentage =
 tcData.total === 0 ? 0 : (tcData.approved / tcData.total) * 100;

 if (loading) return <p>Loading Testing & Commissioning...</p>;

 return (
 <Row className="g-4">

 {/* METRIC SUMMARY */}
 <Col md={6} lg={4}>
 <Card className="shadow-sm border-0 rounded-3">
 <Card.Body>
 <h5 className="fw-bold mb-3">TC Overview</h5>

 <div className="d-flex flex-column gap-1">
 <div>Total Reports: <strong>{tcData.total}</strong></div>
 <div className="text-success">Approved: <strong>{tcData.approved}</strong></div>
 <div className="text-warning">Pending: <strong>{tcData.pending}</strong></div>
 <div className="text-danger">NC Raised: <strong>{tcData.ncRaised}</strong></div>
 <div className="text-info">Retest Required: <strong>{tcData.retest}</strong></div>
 </div>

 <hr />

 <ProgressBar
 now={approvalPercentage}
 label={`${approvalPercentage.toFixed(0)}% Passed`}
 style={{ height: "10px" }}
 />
 </Card.Body>
 </Card>
 </Col>

 {/* STATUS CHART */}
 <Col md={6} lg={4}>
 <Card className="shadow-sm border-0 rounded-3">
 <Card.Body>
 <h5 className="fw-bold mb-3">Status Distribution</h5>

 <PieChart width={270} height={270}>
 <Pie
 data={pieData}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={90}
 label
 >
 {pieData.map((entry, i) => (
 <Cell key={i} fill={CHART_COLORS[entry.name]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </Card.Body>
 </Card>
 </Col>

 {/* RECENT REPORT CARDS */}
 <Col md={12}>
 <Card className="shadow-sm border-0 rounded-3">
 <Card.Body>
 <h5 className="fw-bold mb-3">Recent TC Reports</h5>

 <Row>
 {recentTCs.map((tc) => (
 <Col lg={3} md={4} sm={6} key={tc.id} className="mb-3">
 <Card className="shadow-sm border-0 rounded-3 h-100">
 <Card.Body className="d-flex flex-column">

 {/* Header Row */}
 <div className="d-flex justify-content-between mb-2">
 <strong>{tc.number}</strong>
 <Badge bg={STATUS_COLORS[tc.status]} className="rounded-pill">{tc.status}</Badge>
 </div>

 {/* Material / Equipment Names */}
 <div className="small text-dark mb-2">
 <strong>Materials:</strong><br/>
 <span className="text-muted">
 {tc.materials.slice(0, 3).join(", ")}
 {tc.materials.length > 3 && " + more..."}
 </span>
 </div>

 {/* Meta Info */}
 <div className="small text-muted mb-1">
 <strong>Project:</strong> {tc.project}
 </div>

 <div className="small text-muted mb-1">
 <strong>Items:</strong> {tc.itemCount}
 </div>

 <div className="small text-muted mb-1">
 <strong>Date:</strong> {tc.testDate}
 </div>

 <div className="small text-muted mb-2">
 <strong>Submitted By:</strong> {tc.submittedBy}
 </div>

 <div className="mt-auto small text-secondary" style={{ fontStyle: "italic" }}>
 Latest TC Entry
 </div>

 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>

 </Card.Body>
 </Card>
 </Col>

 </Row>
 );
};

export default TCTab;
